import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  TextField,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DownloadIcon from "@mui/icons-material/Download";
import SearchIcon from "@mui/icons-material/Search";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import EventNoteIcon from "@mui/icons-material/EventNote";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import PaymentIcon from "@mui/icons-material/Payment";
import BarChartIcon from "@mui/icons-material/BarChart";
import NotificationsIcon from "@mui/icons-material/Notifications";
import { axiosConfig } from "../../axios/axiosConfig";

// ── dummy screens list (update when API ready) ──────────────────────────────
// const SCREENS = ["Screen-1", "Screen-2", "Screen-3", "Screen-4", "Screen-5"];

// ── Category config — maps API keys to groups ───────────────────────────────
const CATEGORIES = [
  {
    label: "Core Screens",
    accent: "#0d9488",
    bg: "linear-gradient(135deg,#04343a 0%,#065f46 100%)",
    icon: <PhoneAndroidIcon sx={{ fontSize: 16 }} />,
    keys: [
      "Home_Screen",
      "Screener_Screen",
      "Trade_Calls_Screen",
      "Detail_page_Screen",
      "Watchlist_Screen",
      "Top_Trending_Screen",
    ],
  },
  {
    label: "Trading & Signals",
    accent: "#3b82f6",
    bg: "linear-gradient(135deg,#1e3a8a 0%,#1d4ed8 100%)",
    icon: <TrendingUpIcon sx={{ fontSize: 16 }} />,
    keys: [
      "Options_Screen",
      "Trade_Advisory_Screen",
      "Trade_Performance_Screen",
      "Automate_Signal",
      "RA_Automate_Signal",
      "Stocks_Predictions_Screen",
      "Niftyfifty_Screen",
    ],
  },
  {
    label: "Subscription & Payments",
    accent: "#a855f7",
    bg: "linear-gradient(135deg,#6b21a8 0%,#7c3aed 100%)",
    icon: <PaymentIcon sx={{ fontSize: 16 }} />,
    keys: [
      "Subscription_Screen",
      "Your_Plan_Detail",
      "thankyou_event",
      "thankyou_event_fail",
      "thankyou_event_topup",
      "thankyou_event_topup_fail",
    ],
  },
  {
    label: "Alerts & Sectors",
    accent: "#f59e0b",
    bg: "linear-gradient(135deg,#92400e 0%,#b45309 100%)",
    icon: <NotificationsIcon sx={{ fontSize: 16 }} />,
    keys: ["Alert_Screen", "Sector_Categories_Screen"],
  },
  {
    label: "User Activity",
    accent: "#f43f5e",
    bg: "linear-gradient(135deg,#9f1239 0%,#be123c 100%)",
    icon: <PeopleAltIcon sx={{ fontSize: 16 }} />,
    keys: ["otp_verify_success", "Update_App"],
  },
];

const LABEL_MAP = {
  Trade_Calls_Screen: "Premium Research Call",
  Detail_page_Screen: "Stock Detail Page",
  Trade_Advisory_Screen: "Mid/ Long Term Trade",
  Trade_Performance_Screen: "Screener Performances",
  thankyou_event: "Subscription Event",
  thankyou_event_fail: "Subscription Failed Event",
  thankyou_event_topup: "Subscription Event Topup",
  thankyou_event_topup_fail: "Subscription Event Topup Failed",
  Alert_Screen: "Signals",
};
const fmtKey = (key) => LABEL_MAP[key] || key;

const knownKeys = new Set(CATEGORIES.flatMap((c) => c.keys));
const fmtNum = (n) => (n != null ? Number(n).toLocaleString("en-IN") : "—");

// ── Single metric card ───────────────────────────────────────────────────────
const MetricCard = ({ label, value, max, accent }) => {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <Box
      sx={{
        background: "#fff",
        borderRadius: "10px",
        p: "14px 16px 12px",
        border: "1px solid #e2e8f0",
        display: "flex",
        flexDirection: "column",
        gap: 0.8,
        transition: "box-shadow 0.18s, transform 0.18s",
        cursor: "default",
        "&:hover": {
          boxShadow: "0 6px 24px rgba(0,0,0,0.09)",
          transform: "translateY(-2px)",
        },
      }}
    >
      <Typography
        sx={{
          fontSize: 10,
          fontWeight: 700,
          color: "#94a3b8",
          textTransform: "uppercase",
          letterSpacing: 0.6,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {fmtKey(label)}
      </Typography>
      <Typography
        sx={{
          fontSize: 22,
          fontWeight: 800,
          color: "#0f172a",
          lineHeight: 1,
          letterSpacing: -0.5,
        }}
      >
        {fmtNum(value)}
      </Typography>
      <Box
        sx={{
          mt: 0.4,
          height: 3,
          borderRadius: 2,
          background: "#f1f5f9",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            height: "100%",
            width: `${pct}%`,
            background: accent,
            borderRadius: 2,
            transition: "width 0.7s ease",
          }}
        />
      </Box>
      <Typography sx={{ fontSize: 10, color: "#cbd5e1", textAlign: "right" }}>
        {pct}% of peak
      </Typography>
    </Box>
  );
};

// ── Category section ─────────────────────────────────────────────────────────
const CategorySection = ({ cat, data }) => {
  const entries = cat.keys
    .map((k) => [k, data[k]])
    .filter(([, v]) => v != null);
  if (entries.length === 0) return null;
  const max = Math.max(...entries.map(([, v]) => v));
  const total = entries.reduce((s, [, v]) => s + v, 0);

  return (
    <Box>
      {/* Header row */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: "8px",
              background: cat.bg,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
            }}
          >
            {cat.icon}
          </Box>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#1e293b" }}>
            {cat.label}
          </Typography>
          <Box
            sx={{
              px: 1,
              py: 0.2,
              borderRadius: "20px",
              background: "#f1f5f9",
              fontSize: 11,
              fontWeight: 700,
              color: "#64748b",
            }}
          >
            {entries.length}
          </Box>
        </Box>
        <Typography sx={{ fontSize: 11, fontWeight: 700, color: cat.accent }}>
          Total: {fmtNum(total)}
        </Typography>
      </Box>

      {/* Cards */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))",
          gap: 1.5,
        }}
      >
        {entries.map(([key, value]) => (
          <MetricCard
            key={key}
            label={key}
            value={value}
            max={max}
            accent={cat.accent}
          />
        ))}
      </Box>
    </Box>
  );
};

// ── Main Page ────────────────────────────────────────────────────────────────
const AppEventsPage = () => {
  const navigate = useNavigate();

  // ── Event Log Details ───────────────────────────────────────────────────
  const [eventLog, setEventLog] = useState(null);
  const [eventLogLoading, setEventLogLoading] = useState(true);

  // ── Filters ─────────────────────────────────────────────────────────────
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [appliedFrom, setAppliedFrom] = useState("");
  const [appliedTo, setAppliedTo] = useState("");
  const [downloading, setDownloading] = useState(false);

  // ── App Base User Visit Count (commented out) ───────────────────────────
  // const [baseCount, setBaseCount] = useState(null);
  // const [baseLoading, setBaseLoading] = useState(true);

  // ── User Base Unique Visit Count (commented out) ────────────────────────
  // const [uniqueVisits, setUniqueVisits] = useState([]);
  // const [uniqueLoading, setUniqueLoading] = useState(true);
  // const [uniquePage, setUniquePage] = useState(0);
  // const [uniqueRowsPerPage, setUniqueRowsPerPage] = useState(20);

  // ── Daywise Unique User Visit (commented out) ───────────────────────────
  // const [daywiseData, setDaywiseData] = useState([]);
  // const [daywiseLoading, setDaywiseLoading] = useState(true);
  // const [daywisePage, setDaywisePage] = useState(0);
  // const [daywiseRowsPerPage, setDaywiseRowsPerPage] = useState(20);

  useEffect(() => {
    const fetchEventLog = async () => {
      try {
        setEventLogLoading(true);
        const params = new URLSearchParams();
        if (appliedFrom) params.append("from_date", appliedFrom);
        if (appliedTo) params.append("to_date", appliedTo);
        const res = await axiosConfig({
          method: "get",
          url: `/investors/event-log-details/?${params}`,
        });
        const raw = res?.data;
        const eventData = raw?.data || raw?.result || raw?.results || (typeof raw === "object" && !Array.isArray(raw) ? raw : null);
        const filtered = eventData
          ? Object.fromEntries(Object.entries(eventData).filter(([, v]) => typeof v === "number"))
          : null;
        setEventLog(filtered && Object.keys(filtered).length > 0 ? filtered : null);
      } catch (e) {
        console.error("event log error:", e);
      } finally {
        setEventLogLoading(false);
      }
    };
    fetchEventLog();
  }, [appliedFrom, appliedTo]);

  // fetch base visit count (commented out)
  // useEffect(() => { ... }, []);

  // fetch user base unique visit count (commented out)
  // useEffect(() => { ... }, [appliedFrom, appliedTo]);

  // fetch daywise unique user visit (commented out)
  // useEffect(() => { ... }, [appliedFrom, appliedTo]);

  const handleSearch = () => {
    setAppliedFrom(fromDate);
    setAppliedTo(toDate);
  };

  const handleDownload = async () => {
    try {
      setDownloading(true);
      const params = new URLSearchParams();
      const dlFrom = fromDate || appliedFrom;
      const dlTo = toDate || appliedTo;
      if (dlFrom) params.append("from_date", dlFrom);
      if (dlTo) params.append("to_date", dlTo);
      params.append("download", "true");
      const res = await axiosConfig({
        method: "get",
        url: `/investors/event-log-details/?${params}`,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `event_log_details_${appliedFrom || "all"}_${appliedTo || "all"}.csv`.toLowerCase());
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) {
      console.error("download error:", e);
    } finally {
      setDownloading(false);
    }
  };

  // const fmtDate = (d) => ...
  // const uniquePaginated = ...
  // const daywisePaginated = ...

  const totalEvents = eventLog
    ? Object.values(eventLog).reduce((s, v) => s + (v || 0), 0)
    : 0;
  const topEvent = eventLog
    ? Object.entries(eventLog).sort((a, b) => b[1] - a[1])[0]
    : null;
  const extraEntries = eventLog
    ? Object.entries(eventLog).filter(([k]) => !knownKeys.has(k))
    : [];

  return (
    <Box sx={{ background: "#f1f5f9", minHeight: "100vh" }}>
      {/* ── Page Header with filters ── */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 100,
          background: "#fff",
          borderBottom: "1px solid #e2e8f0",
          px: 3,
          py: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        {/* Left: back + title */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            onClick={() => navigate("/investor")}
            sx={{
              width: 34,
              height: 34,
              borderRadius: "8px",
              border: "1px solid #e2e8f0",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              "&:hover": { background: "#f1f5f9" },
            }}
          >
            <ArrowBackIcon sx={{ fontSize: 18, color: "#64748b" }} />
          </Box>
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: "10px",
              background: "#04343a",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <EventNoteIcon sx={{ color: "#fff", fontSize: 20 }} />
          </Box>
          <Box>
            <Typography
              sx={{
                fontSize: 20,
                fontWeight: 800,
                color: "#0f172a",
                lineHeight: 1.2,
              }}
            >
              App Events
            </Typography>
            <Typography sx={{ fontSize: 12, color: "#94a3b8" }}>
              Screen-wise user visit analytics
            </Typography>
          </Box>
        </Box>

        {/* Right: filters */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
          }}
        >
          <TextField
            type="date"
            size="small"
            label="From"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{
              width: 150,
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                fontSize: 13,
                background: "#fff",
              },
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
            }}
          />
          <TextField
            type="date"
            size="small"
            label="To"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{
              width: 150,
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                fontSize: 13,
                background: "#fff",
              },
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
            }}
          />
          <Button
            variant="contained"
            size="small"
            startIcon={<FilterAltIcon sx={{ fontSize: 16 }} />}
            onClick={handleSearch}
            sx={{
              height: 36,
              borderRadius: "8px",
              background: "#04343a",
              textTransform: "none",
              fontSize: 13,
              fontWeight: 600,
              px: 2,
              "&:hover": { background: "#016551" },
            }}
          >
            Filter
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={
              downloading ? (
                <CircularProgress size={14} sx={{ color: "#fff" }} />
              ) : (
                <DownloadIcon sx={{ fontSize: 16 }} />
              )
            }
            onClick={handleDownload}
            disabled={downloading}
            sx={{
              height: 36,
              borderRadius: "8px",
              background: "#016551",
              textTransform: "none",
              fontSize: 13,
              fontWeight: 600,
              px: 2,
              boxShadow: "0 4px 12px rgba(1,101,81,0.3)",
              "&:hover": { background: "#065f46" },
            }}
          >
            {downloading ? "Downloading…" : "Download"}
          </Button>
        </Box>
      </Box>

      {/* ── Body ── */}
      <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 3 }}>
        {eventLogLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
            <CircularProgress size={36} sx={{ color: "#04343a" }} />
          </Box>
        ) : !eventLog ? (
          <Box sx={{ textAlign: "center", py: 10 }}>
            <Typography sx={{ fontSize: 14, color: "#94a3b8" }}>
              No data found
            </Typography>
          </Box>
        ) : (
          <>
            {/* ── KPI Summary strip ── */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))",
                gap: 1.5,
              }}
            >
              <Box
                sx={{
                  background: "linear-gradient(135deg,#04343a 0%,#065f46 100%)",
                  borderRadius: "12px",
                  p: "18px 20px",
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: "10px",
                    background: "rgba(255,255,255,0.12)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <BarChartIcon sx={{ color: "#fff", fontSize: 22 }} />
                </Box>
                <Box>
                  <Typography
                    sx={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: "rgba(255,255,255,0.55)",
                      textTransform: "uppercase",
                      letterSpacing: 0.8,
                    }}
                  >
                    Total Interactions
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 26,
                      fontWeight: 800,
                      color: "#fff",
                      lineHeight: 1.1,
                      letterSpacing: -0.5,
                    }}
                  >
                    {fmtNum(totalEvents)}
                  </Typography>
                </Box>
              </Box>

              {topEvent && (
                <Box
                  sx={{
                    background:
                      "linear-gradient(135deg,#1e3a8a 0%,#1d4ed8 100%)",
                    borderRadius: "12px",
                    p: "18px 20px",
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                  }}
                >
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: "10px",
                      background: "rgba(255,255,255,0.12)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <TrendingUpIcon sx={{ color: "#fff", fontSize: 22 }} />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: "rgba(255,255,255,0.55)",
                        textTransform: "uppercase",
                        letterSpacing: 0.8,
                      }}
                    >
                      Top Screen
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 22,
                        fontWeight: 800,
                        color: "#fff",
                        lineHeight: 1.1,
                        letterSpacing: -0.5,
                      }}
                    >
                      {fmtNum(topEvent[1])}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 10,
                        color: "rgba(255,255,255,0.5)",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {fmtKey(topEvent[0])}
                    </Typography>
                  </Box>
                </Box>
              )}

              <Box
                sx={{
                  background: "linear-gradient(135deg,#6b21a8 0%,#7c3aed 100%)",
                  borderRadius: "12px",
                  p: "18px 20px",
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: "10px",
                    background: "rgba(255,255,255,0.12)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <EventNoteIcon sx={{ color: "#fff", fontSize: 22 }} />
                </Box>
                <Box>
                  <Typography
                    sx={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: "rgba(255,255,255,0.55)",
                      textTransform: "uppercase",
                      letterSpacing: 0.8,
                    }}
                  >
                    Event Types
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 26,
                      fontWeight: 800,
                      color: "#fff",
                      lineHeight: 1.1,
                      letterSpacing: -0.5,
                    }}
                  >
                    {Object.keys(eventLog).length}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* ── Divider ── */}
            <Box sx={{ borderTop: "1px solid #e2e8f0" }} />

            {/* ── Category sections ── */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {CATEGORIES.map((cat) => (
                <CategorySection key={cat.label} cat={cat} data={eventLog} />
              ))}

              {/* Uncategorised / new keys from API */}
              {extraEntries.length > 0 && (
                <CategorySection
                  cat={{
                    label: "Other Events",
                    accent: "#94a3b8",
                    bg: "linear-gradient(135deg,#334155 0%,#475569 100%)",
                    icon: <BarChartIcon sx={{ fontSize: 16 }} />,
                    keys: extraEntries.map(([k]) => k),
                  }}
                  data={eventLog}
                />
              )}
            </Box>
          </>
        )}

        {/* ── Section 2: User Base Unique Visit Count (commented out) ── */}
        {/* <Paper elevation={0} sx={{ borderRadius: "14px", border: "1px solid #e2e8f0", background: "#fff", boxShadow: "0 1px 6px rgba(0,0,0,0.06)", overflow: "hidden" }}>
          <Box sx={{ px: 2.5, py: 1.8, borderBottom: "1px solid #f1f5f9", background: "#04343a", borderRadius: "14px 14px 0 0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#fff", letterSpacing: 0.5 }}>USER BASE UNIQUE VISIT COUNT</Typography>
            {!uniqueLoading && <Typography sx={{ fontSize: 12, color: "#94a3b8" }}>{uniqueVisits.length.toLocaleString("en-IN")} records</Typography>}
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ background: "#f8fafc" }}>
                  {["#", "Date", "User ID", ...SCREENS].map((h) => (
                    <TableCell key={h} sx={{ fontSize: 11, fontWeight: 700, color: "#64748b", py: 1.2, whiteSpace: "nowrap" }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {uniqueLoading ? (
                  <TableRow><TableCell colSpan={8} sx={{ textAlign: "center", py: 5 }}><CircularProgress size={28} sx={{ color: "#04343a" }} /></TableCell></TableRow>
                ) : uniquePaginated.length === 0 ? (
                  <TableRow><TableCell colSpan={8} sx={{ textAlign: "center", py: 4, fontSize: 13, color: "#94a3b8" }}>No data found</TableCell></TableRow>
                ) : (
                  uniquePaginated.map((row, i) => (
                    <TableRow key={i} sx={{ "&:hover": { background: "#f8fafc" } }}>
                      <TableCell sx={{ fontSize: 12, color: "#64748b" }}>{uniquePage * uniqueRowsPerPage + i + 1}</TableCell>
                      <TableCell sx={{ fontSize: 12, color: "#64748b", whiteSpace: "nowrap" }}>{fmtDate(row.date)}</TableCell>
                      <TableCell sx={{ fontSize: 12, fontWeight: 600, color: "#0f172a" }}>{row.user_id || row.userid || "—"}</TableCell>
                      {[1, 2, 3, 4, 5].map((n) => (
                        <TableCell key={n} sx={{ fontSize: 12, textAlign: "center" }}>
                          <Chip
                            label={row[`screen_${n}`] ?? "—"}
                            size="small"
                            sx={{
                              fontSize: 10, fontWeight: 700, height: 20,
                              background: row[`screen_${n}`] === 1 ? "#dcfce7" : "#f1f5f9",
                              color: row[`screen_${n}`] === 1 ? "#16a34a" : "#94a3b8",
                            }}
                          />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {!uniqueLoading && uniqueVisits.length > 0 && (
            <TablePagination
              component="div"
              count={uniqueVisits.length}
              page={uniquePage}
              onPageChange={(_, p) => setUniquePage(p)}
              rowsPerPage={uniqueRowsPerPage}
              onRowsPerPageChange={(e) => { setUniqueRowsPerPage(parseInt(e.target.value, 10)); setUniquePage(0); }}
              rowsPerPageOptions={[20, 50, 100]}
              sx={{ borderTop: "1px solid #f1f5f9", fontSize: 12 }}
            />
          )}
        </Paper> */}

        {/* ── Section 3: Daywise Unique User Visit (commented out) ── */}
        {/* <Paper elevation={0} sx={{ borderRadius: "14px", border: "1px solid #e2e8f0", background: "#fff", boxShadow: "0 1px 6px rgba(0,0,0,0.06)", overflow: "hidden" }}>
          <Box sx={{ px: 2.5, py: 1.8, borderBottom: "1px solid #f1f5f9", background: "#016551", borderRadius: "14px 14px 0 0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#fff", letterSpacing: 0.5 }}>DAYWISE UNIQUE USER VISIT</Typography>
            {!daywiseLoading && <Typography sx={{ fontSize: 12, color: "#a7f3d0" }}>{daywiseData.length.toLocaleString("en-IN")} records</Typography>}
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ background: "#f8fafc" }}>
                  {["#", "Date", "User ID", ...SCREENS].map((h) => (
                    <TableCell key={h} sx={{ fontSize: 11, fontWeight: 700, color: "#64748b", py: 1.2, whiteSpace: "nowrap" }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {daywiseLoading ? (
                  <TableRow><TableCell colSpan={8} sx={{ textAlign: "center", py: 5 }}><CircularProgress size={28} sx={{ color: "#016551" }} /></TableCell></TableRow>
                ) : daywisePaginated.length === 0 ? (
                  <TableRow><TableCell colSpan={8} sx={{ textAlign: "center", py: 4, fontSize: 13, color: "#94a3b8" }}>No data found</TableCell></TableRow>
                ) : (
                  daywisePaginated.map((row, i) => (
                    <TableRow key={i} sx={{ "&:hover": { background: "#f8fafc" } }}>
                      <TableCell sx={{ fontSize: 12, color: "#64748b" }}>{daywisePage * daywiseRowsPerPage + i + 1}</TableCell>
                      <TableCell sx={{ fontSize: 12, color: "#64748b", whiteSpace: "nowrap" }}>{fmtDate(row.date)}</TableCell>
                      <TableCell sx={{ fontSize: 12, fontWeight: 600, color: "#0f172a" }}>{row.user_id || row.userid || "—"}</TableCell>
                      {[1, 2, 3, 4, 5].map((n) => (
                        <TableCell key={n} sx={{ fontSize: 12, textAlign: "center" }}>
                          <Chip
                            label={row[`screen_${n}`] ?? "—"}
                            size="small"
                            sx={{
                              fontSize: 10, fontWeight: 700, height: 20,
                              background: row[`screen_${n}`] === 1 ? "#dcfce7" : "#f1f5f9",
                              color: row[`screen_${n}`] === 1 ? "#16a34a" : "#94a3b8",
                            }}
                          />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {!daywiseLoading && daywiseData.length > 0 && (
            <TablePagination
              component="div"
              count={daywiseData.length}
              page={daywisePage}
              onPageChange={(_, p) => setDaywisePage(p)}
              rowsPerPage={daywiseRowsPerPage}
              onRowsPerPageChange={(e) => { setDaywiseRowsPerPage(parseInt(e.target.value, 10)); setDaywisePage(0); }}
              rowsPerPageOptions={[20, 50, 100]}
              sx={{ borderTop: "1px solid #f1f5f9", fontSize: 12 }}
            />
          )}
        </Paper> */}
      </Box>
    </Box>
  );
};

export default AppEventsPage;
